import ConciliationStatusChip from "modules/finops/conciliaciones/components/ConciliationStatusChip";
import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  IconButton,
  Tooltip,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Close,
  EditNote,
  Refresh,
  Visibility,
  Search,
  ReceiptLong,
  TaskAlt,
} from "@mui/icons-material";
import DashboardLayout from "app/layout/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/layout/DashboardNavbar";
import Footer from "app/layout/Footer";
import MDButton from "shared/components/MDButton";
import useInfoConcesion from "./useInfoConcesion";
import { sendConcessionResponse, validateConcessionResponse } from "./sendConcessionResponse";
import ConcessionResponse from "./ConcessionResponse";
import ConcessionDocuments from "./ConcessionDocuments";
import ConcessionPeajes from "./ConcessionPeajes";
import { getConcessionResponses } from "./getConcessionResponses";
import { buildPeajePayment, reportedPeajeDetails } from "./peajePayment";

const ConcessionDetail = lazy(() => import("./ConcessionDetail"));
const formatearCantidad = (value) =>
  value == null ? "—" : new Intl.NumberFormat("es-CO").format(value);
const formatearValorGopass = (value) =>
  value == null || String(value).trim() === "" || !Number.isFinite(Number(value))
    ? "—"
    : new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(Number(value));
const buttonSx = { "&&": { fontSize: 13, fontWeight: 700, textTransform: "none", minHeight: 40 } };
const actionIconSx = {
  width: 32,
  height: 32,
  borderRadius: "8px",
  "& .MuiSvgIcon-root": { fontSize: 18 },
  border: "1px solid",
  borderColor: "divider",
  color: "text.secondary",
  bgcolor: "background.paper",
  "&:hover": { bgcolor: "action.hover", color: "info.main", borderColor: "info.main" },
};
const visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};
const inputSx = { "& .MuiInputBase-root": { minHeight: 54, fontSize: 16 } };

export default function InformacionConcesion() {
  const {
    codigo,
    concesion,
    registros,
    loading,
    error,
    aviso,
    progreso,
    cargar,
    scope,
    puedeRegistrar,
    consultaGeneral,
    comercios,
    codigoSeleccionado,
    seleccionarComercio,
    cargandoComercios,
    errorComercios,
    cargarComercios,
  } = useInfoConcesion();
  const [busqueda, setBusqueda] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState(null);
  const [peajes, setPeajes] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState("");
  const [enviado, setEnviado] = useState(false);
  const [respuestas, setRespuestas] = useState({});
  const [consultaRespuestas, setConsultaRespuestas] = useState(null);
  const [revisionRespuestas, setRevisionRespuestas] = useState(0);
  const consultarRespuestas = Boolean(concesion && codigo);
  const respuestaKey = (item) => JSON.stringify([scope, item.concesion, item.key_concil]);
  useEffect(() => {
    if (!consultarRespuestas) {
      setConsultaRespuestas(null);
      setRespuestas({});
      return undefined;
    }
    const controller = new AbortController();
    setConsultaRespuestas({ scope, codigo, loading: true, rows: [], error: "" });
    getConcessionResponses(codigo, controller.signal).then(
      (rows) => {
        if (!controller.signal.aborted)
          setConsultaRespuestas({ scope, codigo, loading: false, rows, error: "" });
      },
      (error) => {
        if (!controller.signal.aborted)
          setConsultaRespuestas({
            scope,
            codigo,
            loading: false,
            rows: [],
            error: error.response?.data?.message || error.message,
          });
      }
    );
    return () => controller.abort();
  }, [scope, codigo, consultarRespuestas, revisionRespuestas]);
  const consultaVigente =
    consultaRespuestas?.scope === scope && consultaRespuestas.codigo === codigo;
  const respuestaPara = (item) => {
    const local = respuestas[respuestaKey(item)];
    const saved = consultaVigente
      ? consultaRespuestas.rows.find(
          (value) =>
            (value.key_concil === item.key_concil ||
              value.key_concil.replace(/^PRE_/, "") === item.key_concil.replace(/^PRE_/, "")) &&
            value.code === String(item.concesion)
        )
      : null;
    return local || saved
      ? {
          ...(local || saved),
          reports: [
            ...(saved?.reports || (saved ? [saved] : [])),
            ...(local?.reports || (local ? [local] : [])),
          ],
        }
      : null;
  };
  const puedeCompletar = (item) => {
    const response = respuestaPara(item);
    if (!response) return true;
    if (!Object.keys(response.more || {}).length) return false;
    const saved = reportedPeajeDetails(response);
    const names = item.peajesEsperados?.length
      ? item.peajesEsperados
      : Object.keys(response.more || {}).filter((name) => name !== "observation");
    return names.some((name) => !saved[name]);
  };
  const envio = useRef(null);
  const contexto = useRef(null);
  contexto.current = { scope, puedeRegistrar };
  useEffect(() => {
    setBusqueda("");
    setPage(0);
    setSelected(null);
    setPeajes([]);
    setErrorEnvio("");
    setEnviado(false);
    setRespuestas({});
    setEnviando(false);
    return () => {
      envio.current?.abort();
      envio.current = null;
    };
  }, [scope, puedeRegistrar]);
  const query = busqueda.trim().toLocaleLowerCase("es");
  const filtrados = registros.filter((item) =>
    [item.key_concil, item.realName, item.concesion].some((value) =>
      value.toLocaleLowerCase("es").includes(query)
    )
  );
  const pagina = Math.min(page, Math.max(0, Math.ceil(filtrados.length / rowsPerPage) - 1));
  const row =
    selected?.scope === scope && (selected.tipo !== "registro" || puedeRegistrar)
      ? registros.find((item) => item.key_concil === selected.key)
      : null;
  const respuesta = row ? respuestaPara(row) : null;
  const abrir = (item, tipo) => {
    if (envio.current) return;
    if (tipo === "registro" && (!puedeRegistrar || !puedeCompletar(item))) return;
    setPeajes([]);
    setErrorEnvio("");
    setEnviado(false);
    setSelected({ key: item.key_concil, scope, tipo });
  };
  const cierreBloqueado = enviando;
  const cerrar = () => {
    if (envio.current || cierreBloqueado) return;
    setSelected(null);
  };
  const enviar = async (peajeRequest) => {
    if (
      envio.current ||
      enviado ||
      !puedeRegistrar ||
      selected?.tipo !== "registro" ||
      selected.scope !== scope ||
      !row ||
      !puedeCompletar(row) ||
      String(row.concesion) !== String(codigo)
    )
      return;
    setErrorEnvio("");
    const targetPeaje = peajeRequest?.peaje;
    const values = {
      key_concil: row.key_concil,
      code: codigo,

      observation: peajeRequest?.observation || "",
      file: peajeRequest?.file,
    };
    const preparar = (response) => {
      const payment = buildPeajePayment(peajes, targetPeaje, response);
      Object.assign(values, {
        more: payment.more,
        steps: payment.steps,
        value_concil: payment.value_concil,
        peajePayment: { rows: peajes, peaje: targetPeaje, response, complete: payment.complete },
      });
    };
    try {
      preparar(respuesta);
      validateConcessionResponse(values);
    } catch (error) {
      setErrorEnvio(error.message);
      return;
    }
    const controller = new AbortController();
    envio.current = controller;
    setEnviando(true);
    try {
      const fresh = await getConcessionResponses(codigo, controller.signal);
      if (controller.signal.aborted) return;
      const reports = fresh
        .filter(
          (record) => record.key_concil.replace(/^PRE_/, "") === row.key_concil.replace(/^PRE_/, "")
        )
        .flatMap((record) => record.reports || [record]);
      setConsultaRespuestas({ scope, codigo, loading: false, rows: fresh, error: "" });
      preparar({ reports: [...(respuesta?.reports || []), ...reports] });
      validateConcessionResponse(values);
      await sendConcessionResponse(values, controller.signal);
      if (
        !controller.signal.aborted &&
        contexto.current.scope === scope &&
        contexto.current.puedeRegistrar
      ) {
        setRespuestas((previous) => ({
          ...previous,
          [respuestaKey(row)]: {
            steps: values.steps == null ? null : Number(values.steps),
            value_concil: values.value_concil == null ? null : Number(values.value_concil),
            observation: values.observation.trim(),
            fileName: values.file?.name || "",
            more: values.more,
            reports: [
              ...(values.peajePayment.response?.reports || []),
              {
                steps: values.steps,
                value_concil: Number(values.value_concil),
                more: values.more,
                fileName: values.file.name,
                observation: values.observation.trim(),
                createdAt: new Date().toISOString(),
              },
            ],
          },
        }));
        if (values.peajePayment.complete) {
          setSelected({ key: row.key_concil, scope, tipo: "respuesta" });
          setPeajes([]);
        }
        setRevisionRespuestas((value) => value + 1);
      }
    } catch (error) {
      if (!controller.signal.aborted && contexto.current.scope === scope)
        setErrorEnvio(error.message);
    } finally {
      if (envio.current === controller) {
        envio.current = null;
        setEnviando(false);
      }
    }
  };
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card sx={{ mt: 2, mb: 3, overflow: "hidden" }}>
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            background: "linear-gradient(120deg, #123149, #155e66)",
            color: "#fff",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ sm: "center" }}
          >
            <Box>
              <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700 }}>
                {concesion
                  ? `Información de concesión · ${String(concesion.nombre || "").toUpperCase()}`
                  : "Información de concesión"}
              </Typography>
              <Typography sx={{ color: "#fff", opacity: 0.85, fontSize: 14, mt: 0.5 }}>
                {concesion
                  ? `Código ${codigo} · ${
                      consultaGeneral
                        ? "Comercio seleccionado"
                        : "Llaves con conciliación para tu concesión"
                    }`
                  : consultaGeneral
                  ? "Selecciona un comercio para consultar sus conciliaciones"
                  : "Consulta las conciliaciones de tu concesión"}
              </Typography>
            </Box>
            <MDButton
              color="white"
              variant="contained"
              startIcon={<Refresh />}
              disabled={loading}
              onClick={cargar}
              sx={{
                "&&": {
                  ...buttonSx["&&"],
                  color: "#123149",
                  backgroundColor: "#fff",
                  "&.Mui-disabled": { color: "#d5e5ee", backgroundColor: "rgba(255,255,255,0.15)" },
                },
              }}
            >
              {loading ? "Consultando…" : "Actualizar"}
            </MDButton>
          </Stack>
        </Box>
        <Stack spacing={2.5} sx={{ p: { xs: 2, md: 3 } }}>
          {consultaGeneral && (
            <Box
              sx={{
                p: 2.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "background.default",
              }}
            >
              <Typography variant="h6" color="text.primary" sx={{ mb: 0.5 }}>
                Consultar comercio
              </Typography>
              <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
                {puedeRegistrar
                  ? "Selecciona una concesión para consultar sus pasos y novedades o registrar su respuesta."
                  : "Puedes consultar los pasos y novedades de cualquier comercio. Tu rol no permite registrar ni enviar formularios desde esta vista."}
              </Typography>
              <Autocomplete
                options={comercios}
                value={comercios.find((item) => item.code === codigoSeleccionado) || null}
                loading={cargandoComercios}
                disabled={cargandoComercios}
                onChange={(_, item) => seleccionarComercio(item?.code || "")}
                getOptionLabel={(item) => `${item.nombre} · ${item.code}`}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                noOptionsText={
                  errorComercios ? "No se pudo cargar el catálogo" : "No hay comercios disponibles"
                }
                loadingText="Cargando comercios…"
                renderInput={(params) => (
                  <TextField {...params} label="Comercio a consultar" sx={inputSx} />
                )}
              />
              {errorComercios && (
                <Alert
                  severity="error"
                  sx={{ mt: 2 }}
                  action={
                    <MDButton color="info" variant="text" onClick={cargarComercios}>
                      Reintentar
                    </MDButton>
                  }
                >
                  {errorComercios}
                </Alert>
              )}
            </Box>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {consultaVigente && consultaRespuestas.loading && (
            <Typography role="status" sx={{ fontSize: 13, color: "text.secondary" }}>
              Consultando respuestas guardadas…
            </Typography>
          )}
          {consultaVigente && consultaRespuestas.error && (
            <Alert
              severity="warning"
              action={
                <MDButton
                  color="info"
                  variant="text"
                  onClick={() => setRevisionRespuestas((value) => value + 1)}
                >
                  Reintentar
                </MDButton>
              }
            >
              No se pudieron consultar las respuestas guardadas. {consultaRespuestas.error}
            </Alert>
          )}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6" color="text.primary">
                Conciliaciones disponibles
              </Typography>
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                {puedeRegistrar
                  ? "Abre un registro para completar sus valores o consulta sus pasos y novedades."
                  : "Consulta los pasos y novedades del comercio seleccionado."}
              </Typography>
            </Box>
            <Chip label={`${filtrados.length} llaves`} color="info" variant="outlined" />
          </Stack>
          <TextField
            fullWidth
            label="Buscar por llave, código o concesión"
            value={busqueda}
            onChange={(event) => {
              setBusqueda(event.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...inputSx, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
          {loading && (
            <Box role="status" aria-live="polite">
              <Typography sx={{ mb: 1, fontSize: 13, color: "text.secondary" }}>
                {progreso.total
                  ? `Revisando llaves: ${progreso.done} de ${progreso.total}`
                  : "Consultando la concesión y sus llaves…"}
              </Typography>
              <LinearProgress
                variant={progreso.total ? "determinate" : "indeterminate"}
                value={progreso.total ? (progreso.done / progreso.total) * 100 : 0}
              />
            </Box>
          )}
          <TableContainer
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              "& .MuiTableCell-root": {
                fontSize: 13,
                px: 2,
                py: 1.5,
                textAlign: "center",
                verticalAlign: "middle",
                overflowWrap: "anywhere",
              },
              "& .MuiTableCell-head": {
                bgcolor: "#16485a",
                color: "#fff",
                fontWeight: 700,
                whiteSpace: "nowrap",
                py: 2,
              },
            }}
          >
            <Table
              aria-label="Llaves de conciliación de la concesión"
              sx={{ tableLayout: "fixed", width: "100%", minWidth: 1200 }}
            >
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  {[
                    "Llave de conciliación",
                    "Código",
                    "Concesión",
                    "Cantidad de pasos",
                    "Valor Gopass",
                    "Estado",
                    "Acciones",
                  ].map((label) => (
                    <TableCell key={label} align="center">
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtrados.slice(pagina * rowsPerPage, (pagina + 1) * rowsPerPage).map((item) => (
                  <TableRow
                    key={item.key_concil}
                    hover
                    sx={{
                      "&:nth-of-type(even)": { bgcolor: "action.hover" },
                      "&:last-child td": { borderBottom: 0 },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700, color: "info.main" }}>
                      {item.key_concil}
                    </TableCell>
                    <TableCell>{item.concesion}</TableCell>
                    <TableCell>
                      {String(item.realName || concesion?.nombre || "").toUpperCase()}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      {formatearCantidad(item.countConcil)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      {formatearValorGopass(item.value_gp)}
                    </TableCell>
                    <TableCell>
                      <ConciliationStatusChip value={item.estadoCuadre} />
                    </TableCell>
                    <TableCell sx={{ "&&": { px: 1 } }}>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "auto 32px 32px",
                          gap: 0.75,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Box sx={{ minWidth: 32, minHeight: 32, display: "flex", gap: 0.75 }}>
                          {puedeRegistrar && puedeCompletar(item) && (
                            <Tooltip title="Registrar pasos, valores y Excel de la concesión" arrow>
                              <IconButton
                                aria-label="Registrar"
                                onClick={() => abrir(item, "registro")}
                                sx={{
                                  ...actionIconSx,
                                  color: "info.main",
                                  bgcolor: "rgba(26,115,232,0.07)",
                                  borderColor: "rgba(26,115,232,0.25)",
                                }}
                              >
                                <EditNote />
                                <Box component="span" sx={visuallyHidden}>
                                  Registrar
                                </Box>
                              </IconButton>
                            </Tooltip>
                          )}
                          {respuestaPara(item) && (
                            <Tooltip title="Ver respuesta enviada por la concesión" arrow>
                              <IconButton
                                aria-label="Ver respuesta"
                                onClick={() => abrir(item, "respuesta")}
                                sx={{
                                  ...actionIconSx,
                                  color: "success.main",
                                  bgcolor: "rgba(46,125,50,0.07)",
                                  borderColor: "rgba(46,125,50,0.3)",
                                  "&:hover": {
                                    color: "success.main",
                                    bgcolor: "rgba(46,125,50,0.14)",
                                    borderColor: "success.main",
                                  },
                                }}
                              >
                                <TaskAlt />
                                <Box component="span" sx={visuallyHidden}>
                                  Ver respuesta
                                </Box>
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Tooltip title="Ver pasos y novedades" arrow>
                          <IconButton
                            aria-label="Detalle"
                            onClick={() => abrir(item, "detalle")}
                            sx={actionIconSx}
                          >
                            <ReceiptLong fontSize="small" />
                            <Box component="span" sx={visuallyHidden}>
                              Detalle
                            </Box>
                          </IconButton>
                        </Tooltip>
                        <Box sx={{ width: 32, height: 32 }}>
                          <ConcessionDocuments
                            key={`${scope}-${item.key_concil}-${item.conciliationId}`}
                            refreshing={loading}
                            conciliationId={item.documentsConciliationId || item.conciliationId}
                            code={item.concesion}
                            keyConcil={item.documentsKeyConcil || item.key_concil}
                          />
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {!filtrados.length && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      {consultaGeneral && !codigoSeleccionado
                        ? "Selecciona un comercio para consultar sus conciliaciones."
                        : loading
                        ? "Revisando conciliaciones…"
                        : error || aviso
                        ? "No se ha podido completar la consulta. Pulsa Actualizar para reintentar."
                        : registros.length
                        ? "No hay resultados para esta búsqueda."
                        : "No se encontraron llaves con conciliación para este comercio."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filtrados.length}
            page={pagina}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            onPageChange={(_, value) => setPage(value)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            La cantidad de pasos corresponde al resumen de cada llave y concesión.
          </Typography>
        </Stack>
      </Card>
      <Dialog
        open={Boolean(row)}
        onClose={cerrar}
        disableEscapeKeyDown={cierreBloqueado}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxWidth: selected?.tipo === "detalle" ? 1760 : 680,
            maxHeight: "90dvh",
            m: { xs: 1.5, sm: 4 },
            width:
              selected?.tipo === "detalle"
                ? { xs: "calc(100% - 24px)", sm: "calc(100% - 64px)", lg: "90vw" }
                : { xs: "calc(100% - 24px)", sm: "100%" },
          },
        }}
        aria-labelledby="concesion-dialog-title"
      >
        <DialogTitle
          id="concesion-dialog-title"
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography
                component="span"
                sx={{ fontSize: 20, fontWeight: 700, color: "text.primary", display: "block" }}
              >
                {selected?.tipo === "detalle"
                  ? "Detalle de conciliación"
                  : selected?.tipo === "respuesta"
                  ? "Respuesta de conciliación"
                  : "Registrar conciliación"}
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: 14, color: "text.secondary", display: "block", mt: 0.5 }}
              >
                {String(concesion?.nombre || "").toUpperCase()}
              </Typography>
            </Box>
            <IconButton aria-label="Cerrar modal" onClick={cerrar} disabled={cierreBloqueado}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 }, pt: "24px !important" }}>
          {row && (
            <Box
              sx={{
                p: 2,
                mb: 3,
                bgcolor: "background.default",
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={selected?.tipo === "respuesta" ? 6 : 4}>
                  <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
                    Llave de conciliación
                  </Typography>
                  <Typography sx={{ fontSize: 17, fontWeight: 700, color: "text.primary" }}>
                    {row.key_concil}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={selected?.tipo === "respuesta" ? 6 : 4}>
                  <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
                    Código de concesión
                  </Typography>
                  <Typography sx={{ fontSize: 17, fontWeight: 700, color: "text.primary" }}>
                    {row.concesion}
                  </Typography>
                </Grid>
                {selected?.tipo === "registro" && (
                  <Grid item xs={12} sm={4}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
                      Tipo de pago
                    </Typography>
                    <Chip
                      label={
                        row.tipo_pago === "POR_PEAJE"
                          ? "Por peaje"
                          : row.tipo_pago === "CONSOLIDADO"
                          ? "Consolidado"
                          : "No informado"
                      }
                      size="small"
                      sx={{ bgcolor: "rgba(22,72,90,0.08)", color: "#16485a", fontWeight: 700 }}
                    />
                  </Grid>
                )}
                {selected?.tipo === "detalle" && (
                  <Grid item xs={6} sm={4}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
                      Pasos conciliados
                    </Typography>
                    <Typography sx={{ fontSize: 17, fontWeight: 700, color: "text.primary" }}>
                      {formatearCantidad(row.countConcil)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
          {row && selected.tipo === "respuesta" && respuesta && (
            <ConcessionResponse
              key={`${scope}-${row.key_concil}-${respuesta.id || "local"}`}
              response={respuesta}
              porPeaje={
                Boolean(respuesta.files?.length) ||
                Object.keys(reportedPeajeDetails(respuesta)).length > 0
              }
            />
          )}
          {row && selected.tipo === "detalle" && (
            <Suspense
              fallback={
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <CircularProgress aria-label="Cargando pasos y novedades" />
                </Box>
              }
            >
              <ConcessionDetail
                conciliationId={row.documentsConciliationId || row.conciliationId}
                key={`${scope}-${row.key_concil}`}
                keyConcil={row.key_concil}
                concesion={row.concesion}
                status={row.estadoCuadre}
                valorFinalFinops={row.value_final}
              />
            </Suspense>
          )}
          {row && puedeRegistrar && selected.tipo === "registro" && (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6" color="text.primary">
                  Valores a reportar
                </Typography>
                <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}>
                  La llave y el código corresponden al registro seleccionado y no se modifican.
                </Typography>
              </Box>
              <ConcessionPeajes
                key={`${scope}-${row.key_concil}`}
                code={codigo}
                conciliationId={row.conciliationId}
                detailReference={row.detailReference}
                concesion={row.concesion}
                keyConcil={row.key_concil}
                rows={peajes}
                onChange={setPeajes}
                disabled={enviando || enviado}
                porPeaje
                reportados={reportedPeajeDetails(respuesta)}
                onEnviarPeaje={enviar}
              />
              {errorEnvio && <Alert severity="error">{errorEnvio}</Alert>}
              {enviado ? (
                <Alert severity="success">
                  La respuesta de conciliación se envió correctamente.
                </Alert>
              ) : (
                <Alert severity="info">
                  Los totales de pasos y valor se envían en cero hasta completar todos los peajes.
                  El último envío incluye la suma de los pasos y valores reportados.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: 1,
            borderColor: "divider",
            gap: 1,
            bgcolor: "background.default",
          }}
        >
          <MDButton
            onClick={cerrar}
            disabled={cierreBloqueado}
            color="secondary"
            variant="outlined"
            sx={buttonSx}
          >
            Cerrar
          </MDButton>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}
